import Vuex from 'vuex';
import Vue from 'vue';
import get from 'lodash/get';
import * as Sentry from '@sentry/vue';
import { USER_LOGIN, USER_LOGOUT } from '@/actions/mutations';
import tmsApi from '@/api/tmsApi';
import companies from './companies';
import company_select from './company_select';
import shipment_preset from './shipment_preset';
import users from './users';
import vehicles from './vehicles';
import issues from './issues';
import employees from './employees';
import reports from './reports';
import report_files from './report_files';
import shipment_md from './shipment_md';
import gpsDevices from './gps_devices';
import notifications from './notifications';
import dashboard from './dashboard';
import prestart_checklist from './prestart_checklist';
import question_prestart from './question_prestart';
import location_accuracy from './location_accuracy';
import customers from './customers';
import vehicle_group from './vehicle_group';

const localforage = tmsApi.createStorage();

Vue.use(Vuex);

export default new Vuex.Store({
  modules: {
    companies,
    company_select,
    users,
    vehicles,
    issues,
    shipment_md,
    employees,
    reports,
    report_files,
    gpsDevices,
    notifications,
    dashboard,
    prestart_checklist,
    question_prestart,
    customers,
    shipment_preset,
    location_accuracy,
    vehicle_group,
  },
  state: {
    loggedIn: false,
    sessionId: null,
    browserId: null,
    username: null,
    userData: null,
    token: null,
    currentCompany: null,
    useContainer: true,
    fillHeight: false,
    impersonating: false,
    multipleCompanies: false,
    breadcrumbItems: [],
    showBreadcrumb: true,
    showFooter: true,
    lastBreadcrumbItem: null,
    minimizeSidebar: false,
    fullPageTimer: 0,
    errors: {
      actions: [],
    },
  },
  getters: {
    isLoggedIn(state) {
      return state.loggedIn;
    },
    token(state) {
      return state.token;
    },
    username(state) {
      return state.username;
    },
    userData(state) {
      return state.userData || {};
    },
    sessionId(state) {
      return state.sessionId;
    },
    browserId(state) {
      return state.browserId;
    },
    userAccess(state) {
      return state.userData ? state.userData.account.access : {};
    },
    currentCompany(state) {
      const userData = state.userData || {};
      return state.currentCompany || userData.company_id;
    },
    isCopp(state) {
      if (!state.userData) return false;
      if (!state.userData.company) return false;
      return state.userData.company.external_id === 'COPP-1';
    },
    lastBreadcrumbItem(state) {
      return state.lastBreadcrumbItem;
    },
    breadcrumbItems(state) {
      return state.breadcrumbItems;
    },
    impersonating(state) {
      return state.impersonating;
    },
    minimizeSidebar(state) {
      return state.minimizeSidebar;
    },
    useBreadcrumb(state) {
      return state.showBreadcrumb;
    },
    useFooter(state) {
      return state.showFooter;
    },
    useContainer(state) {
      return state.useContainer;
    },
    fillHeight(state) {
      return state.fillHeight;
    },
  },
  mutations: {
    [USER_LOGIN](state, { data, token }) {
      state.loggedIn = true;
      state.username = data.username;
      state.token = token;
      state.userData = data;
    },
    [USER_LOGOUT](state) {
      state.loggedIn = false;
      state.username = null;
      state.token = null;
    },
    SET_SESSION_ID(state, sessionId) {
      state.sessionId = sessionId;
    },
    SET_BROWSER_ID(state, browserId) {
      state.browserId = browserId;
    },
    SET_LAST_BREADCRUMB_NAME(state, text) {
      state.lastBreadcrumbItem = text;
    },
    CLEAR_BREADCRUMB_ITEM(state) {
      state.lastBreadcrumbItem = null;
    },
    SET_CURRENT_COMPANY(state, clientid) {
      state.currentCompany = clientid;
    },
    SET_BREADCRUMB_ITEMS({ breadcrumbItems }, items) {
      breadcrumbItems.splice(0);
      let to = '';
      items.forEach((item) => {
        to += `/${item.to}`;
        item.to = to.substr(1);
        breadcrumbItems.push(item);
      }, this);
    },
    SHOW_HIDE_BREADCRUMB(state, show) {
      state.showBreadcrumb = show;
    },
    SET_USE_CONTAINER(state, use) {
      state.useContainer = use;
    },
    SET_FILL_HEIGHT(state, fill) {
      state.fillHeight = fill;
    },
    SHOW_HIDE_FOOTER(state, show) {
      state.showFooter = show;
    },
    IMPERSONATE(state) {
      state.impersonating = true;
    },
    STOP_IMPERSONATE(state) {
      state.impersonating = false;
    },
    MINIMIZE_SIDEBAR(state) {
      state.minimizeSidebar = true;
    },
    RESTORE_SIDEBAR(state) {
      state.minimizeSidebar = false;
    },
    ADD_ERROR(state, error) {
      state.errors.actions.push(error);
    },
    REMOVE_ERROR(state, error) {
      const index = state.errors.actions.findIndex(err => err === error);
      state.errors.actions.splice(index, 1);
    },
  },
  actions: {
    SAVE_LOGIN_STATE(context, loginResult) {
      return localforage.setItem('login_result', loginResult).then(() => {
        context.commit(USER_LOGIN, loginResult);
      });
    },
    LOAD_LOGIN_STATE({ commit }) {
      return localforage.getItem('login_result')
        .then((loginResult) => {
          if (!loginResult) { return false; }
          const username = get(loginResult, 'data.username', '');
          Sentry.setUser({ username });
          commit(USER_LOGIN, loginResult);
          return true;
        })
        .then((result) => {
          if (!result) { return result; }
          return localforage.getItem('admin_login_result');
        }).then((adminLoginResult) => {
          if (!adminLoginResult) { return false; }
          commit('IMPERSONATE');
          return true;
        });
    },
    INIT_BROWSER_ID({ commit }) {
      const genBrowserId = Date.now().toString(36).toUpperCase();
      return localforage.getItem('browser_id')
        .then((browserId) => {
          if (browserId) {
            return browserId;
          }
          return localforage.setItem('browser_id', genBrowserId)
            .then(() => genBrowserId);
        })
        .then((browserId) => {
          commit('SET_BROWSER_ID', browserId);
        });
    },
    REMOVE_LOGIN_STATE(context) {
      return localforage.removeItem('login_result')
        .then(() => context.commit(USER_LOGOUT));
    },
    IMPERSONATE({ commit, state }, userId) {
      const api = tmsApi.build(state.token);
      const query = `{
          login(id: ${userId}) {
            token
            data
          }
        }`;
      let newLoginResult;
      return api(query, 'login')
        .then(({ data }) => {
          if (typeof data !== 'object' || data === null) {
            return null;
          }
          newLoginResult = data.login;
          return localforage.getItem('login_result');
        })
        .then((currentLoginData) => {
          if (!currentLoginData) { return false; }
          return localforage.setItem('admin_login_result', currentLoginData);
        })
        .then((result) => {
          if (!result) { return false; }
          return localforage.setItem('login_result', newLoginResult);
        })
        .then(() => {
          commit(USER_LOGIN, newLoginResult);
          commit('IMPERSONATE');
        })
        .catch(() => {
          commit('ADD_ERROR', 'IMPERSONATE');
        });
    },
    STOP_IMPERSONATE({ commit }) {
      let loginResult;

      return localforage.getItem('admin_login_result')
        .then((adminLoginResult) => {
          loginResult = adminLoginResult;
          return localforage.setItem('login_result', adminLoginResult);
        })
        .then(() => localforage.removeItem('admin_login_result'))
        .then(() => {
          commit(USER_LOGIN, loginResult);
          commit('STOP_IMPERSONATE');
          return true;
        })
        .catch(() => {
          commit('ADD_ERROR', 'STOP_IMPERSONATE');
        });
    },
    SET_FULL_PAGE({ commit, state }, full) {
      clearTimeout(state.fullPageTimer);
      state.fullPageTimer = setTimeout(() => {
        commit('SET_FILL_HEIGHT', full);
        commit('SET_USE_CONTAINER', !full);
        commit('SHOW_HIDE_BREADCRUMB', !full);
        commit('SHOW_HIDE_FOOTER', !full);
      }, 300);
    },
  },
});



// WEBPACK FOOTER //
// ./src/store/index.js